/* eslint-disable max-lines */
import {
  WorkerRequirementMappingLevel,
  WorkerRequirementStatus,
} from "@clipboard-health/contract-worker-app-bff";
import { type IconType } from "@clipboard-health/ui-components";
import { API_TYPES } from "@src/appV2/api";
import { DayOfTheWeek } from "@src/appV2/lib/dates/types";
import {
  JobType,
  PLACEMENT_CANDIDATE_BAN_ACTION,
  ShiftNameType,
} from "@src/appV2/redesign/PlacementCandidate/types";
import {
  INTERVIEW_SCHEDULE_STATUS,
  INTERVIEW_TYPE,
  INTERVIEW_VERIFICATION_STATUS,
  InterviewStatus,
} from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { z } from "zod";

export const PLACEMENT_STATUS = [
  "draft",
  "open",
  "archived",
  "waitlist",
  "paused",
  "closed",
] as const;
export type PlacementStatus = (typeof PLACEMENT_STATUS)[number];

export const PLACEMENT_STATUS_MAP = {
  DRAFT: "draft",
  OPEN: "open",
  ARCHIVED: "archived",
  WAITLIST: "waitlist",
  PAUSED: "paused",
  CLOSED: "closed",
} as const;

export enum PlacementApplicationMethod {
  EASY_SCHEDULING = "easy_scheduling",
  INSTANT_INTERVIEW = "instant_interview",
  REQUEST_INTERVIEW = "request_interview",
  APPLY_IN_APP = "apply_in_app",
  APPLY_IN_WEB = "apply_in_web",
}

export enum PlacementApplicationStatus {
  APPLIED = "applied",
  ACCEPTED = "accepted",
  INTERVIEW = "interview",
  INTERVIEW_PENDING = "interview:pending",
  INTERVIEW_NOT_CONFIRMED = "interview:notConfirmed",
  INTERVIEW_CONFIRMED = "interview:confirmed",
  INTERVIEW_CANCELLED = "interview:cancelled",
  INTERVIEW_NOT_SHOWED = "interview:notShowed",
  INTERVIEW_HELD = "interview:held",
  INTERVIEW_TURNED_AWAY = "interview:turnedAway",
  OFFER = "offer",
  OFFER_REJECTED = "offer:rejected", // rejected by the candidate
  HIRED = "hired",
  HIRED_ONBOARDING = "hired:onboarding",
  HIRED_RETAINED = "hired:retained",
  HIRED_RESIGNED = "hired:resigned",
  REJECTED = "rejected", // rejected by the workplace
}

export const CompletedPlacementApplicationStatuses: PlacementApplicationStatus[] = [
  PlacementApplicationStatus.OFFER_REJECTED,
  PlacementApplicationStatus.HIRED,
  PlacementApplicationStatus.HIRED_ONBOARDING,
  PlacementApplicationStatus.HIRED_RETAINED,
  PlacementApplicationStatus.HIRED_RESIGNED,
  PlacementApplicationStatus.REJECTED,
];

export const defaultPlacementApplicationStatusMap: Record<
  PlacementApplicationStatus,
  {
    label: string;
    value: PlacementApplicationStatus;
    color: "info" | "success" | "warning" | "error";
  }
> = {
  [PlacementApplicationStatus.APPLIED]: {
    label: "In review",
    value: PlacementApplicationStatus.APPLIED,
    color: "info" as const,
  },
  [PlacementApplicationStatus.ACCEPTED]: {
    label: "Accepted",
    value: PlacementApplicationStatus.ACCEPTED,
    color: "info" as const,
  },
  [PlacementApplicationStatus.INTERVIEW]: {
    label: "Interview scheduled",
    value: PlacementApplicationStatus.INTERVIEW,
    color: "info" as const,
  },
  [PlacementApplicationStatus.INTERVIEW_PENDING]: {
    label: "Interview pending",
    value: PlacementApplicationStatus.INTERVIEW_PENDING,
    color: "warning" as const,
  },
  [PlacementApplicationStatus.INTERVIEW_NOT_CONFIRMED]: {
    label: "Interview scheduled",
    value: PlacementApplicationStatus.INTERVIEW_NOT_CONFIRMED,
    color: "info" as const,
  },
  [PlacementApplicationStatus.INTERVIEW_CONFIRMED]: {
    label: "Interview scheduled",
    value: PlacementApplicationStatus.INTERVIEW_CONFIRMED,
    color: "info" as const,
  },
  [PlacementApplicationStatus.INTERVIEW_CANCELLED]: {
    label: "Interview cancelled",
    value: PlacementApplicationStatus.INTERVIEW_CANCELLED,
    color: "error" as const,
  },
  [PlacementApplicationStatus.INTERVIEW_NOT_SHOWED]: {
    label: "Interview no showed",
    value: PlacementApplicationStatus.INTERVIEW_NOT_SHOWED,
    color: "info" as const,
  },
  [PlacementApplicationStatus.INTERVIEW_HELD]: {
    label: "Interview held",
    value: PlacementApplicationStatus.INTERVIEW_HELD,
    color: "info" as const,
  },
  [PlacementApplicationStatus.INTERVIEW_TURNED_AWAY]: {
    label: "Interview scheduled",
    value: PlacementApplicationStatus.INTERVIEW_TURNED_AWAY,
    color: "info" as const,
  },
  [PlacementApplicationStatus.OFFER]: {
    label: "Offer made",
    value: PlacementApplicationStatus.OFFER,
    color: "success" as const,
  },
  [PlacementApplicationStatus.OFFER_REJECTED]: {
    label: "Offer",
    value: PlacementApplicationStatus.OFFER_REJECTED,
    color: "success" as const,
  },
  [PlacementApplicationStatus.HIRED]: {
    label: "Offer accepted",
    value: PlacementApplicationStatus.HIRED,
    color: "success" as const,
  },
  [PlacementApplicationStatus.HIRED_ONBOARDING]: {
    label: "Hired", // show as hired
    value: PlacementApplicationStatus.HIRED_ONBOARDING,
    color: "success" as const,
  },
  [PlacementApplicationStatus.HIRED_RETAINED]: {
    label: "Hired", // show as hired
    value: PlacementApplicationStatus.HIRED_RETAINED,
    color: "success" as const,
  },
  [PlacementApplicationStatus.HIRED_RESIGNED]: {
    label: "Hired", // show as hired
    value: PlacementApplicationStatus.HIRED_RESIGNED,
    color: "success" as const,
  },
  [PlacementApplicationStatus.REJECTED]: {
    label: "Rejected",
    value: PlacementApplicationStatus.REJECTED,
    color: "error" as const,
  },
};

export enum PlacementPerk {
  SHIFT_DIFFERENTIAL = "shiftDifferential",
  DAILY_PAY = "dailyPay",
  TWELVE_SHIFTS_AVAILABLE = "twelveShiftsAvailable",
  DOUBLES_AVAILABLE = "doublesAvailable",
  SIGN_ON_BONUS = "signOnBonus",
  OVERTIME_AVAILABLE = "overtimeAvailable",
  FREE_DISCOUNTED_CHILDCARE = "freeDiscountedChildcare",
  NEW_GRAD_FRIENDLY = "newGradFriendly",
}

export const PLACEMENT_PERKS: Array<{
  value: PlacementPerk;
  label: string;
  icon: IconType;
}> = [
  {
    value: PlacementPerk.NEW_GRAD_FRIENDLY,
    label: "New Grad Friendly",
    icon: "school",
  },
  {
    value: PlacementPerk.SHIFT_DIFFERENTIAL,
    label: "Shift Differential",
    icon: "calendar-clock",
  },
  {
    value: PlacementPerk.DAILY_PAY,
    label: "Daily Pay",
    icon: "time-flies",
  },
  {
    value: PlacementPerk.TWELVE_SHIFTS_AVAILABLE,
    label: "12-Hour Shifts Available",
    icon: "date-clock",
  },
  {
    value: PlacementPerk.DOUBLES_AVAILABLE,
    label: "Double Shifts Available",
    icon: "clock-history",
  },
  {
    value: PlacementPerk.SIGN_ON_BONUS,
    label: "Gift",
    icon: "bolt-filled",
  },
  {
    value: PlacementPerk.OVERTIME_AVAILABLE,
    label: "Overtime Available",
    icon: "clock",
  },
  {
    value: PlacementPerk.FREE_DISCOUNTED_CHILDCARE,
    label: "Free/Discounted Childcare",
    icon: "like-filled",
  },
];

export const placementsFilterSchema = z.object({
  distanceInMiles: z
    .object({
      $lt: z.number(),
    })
    .optional(),
  daysOfWeek: z.string().optional(),
  workerTypes: z.string().optional(),
  jobTypes: z.string().optional(),
  shiftTypes: z.string().optional(),
  workplaceName: z.string().optional(),
  workplaceId: z.string().optional(),
  "applications.status": z.string().optional(),
  hasInterviews: z.boolean().optional(),
  ageGroups: z.string().optional(), // UI field name differs from backend
  tags: z.string().optional(), // Backend field name
  applicationMethod: z.string().optional(), // Filter placements by application method (e.g., easy_scheduling)
  state: z.string().optional(), // Filter placements by US state code (e.g., "MO,IL")
  includeClosedPlacementsKeepingInterviewsAndCandidates: z.boolean().optional(),
});

export enum PlacementSortOption {
  INTERVIEW_START_DESC = "-interviews.start",
  INTERVIEW_START = "interviews.start",
  CREATED_AT_DESC = "-createdAt",
  CREATED_AT = "createdAt",
  APPLICATIONS_APPLIED_DATE_DESC = "-applications.appliedDate",
  APPLICATIONS_APPLIED_DATE = "applications.appliedDate",
}

export const getPlacementsForWorkplaceQuerySchema = z.object({
  filter: placementsFilterSchema.optional(),
  sort: z.nativeEnum(PlacementSortOption).optional(),
  page: z
    .object({
      size: z.number().optional(),
      cursor: z.string().optional(),
    })
    .optional(),
});
export type GetPlacementsForWorkplaceQuery = z.infer<typeof getPlacementsForWorkplaceQuerySchema>;

export const transparencyPayRateBaseRateSchema = z.object({
  baseRate: z.number(),
  minYearsOfExperience: z.number(),
});

export const transparencyPayRateDifferentialsSchema = z.object({
  benefitsDifferential: z.number().optional(),
  healthBenefitsDifferential: z.number().optional(),
  ptoBenefitsDifferential: z.number().optional(),
  weekendDifferential: z.number().optional(),
  dayDifferential: z.number().optional(),
  eveningDifferential: z.number().optional(),
  nightDifferential: z.number().optional(),
});

export const transparencyPayRatesSchema = z.object({
  baseRates: z.array(transparencyPayRateBaseRateSchema),
  differentials: transparencyPayRateDifferentialsSchema.optional(),
});

export const jobShiftTransparencyPayRatesSchema = z.object({
  baseRates: z.array(transparencyPayRateBaseRateSchema).optional(),
  differentials: transparencyPayRateDifferentialsSchema.optional(),
});

export const jobShiftSchema = z.object({
  jobType: z.nativeEnum(JobType),
  shiftTypes: z.array(z.nativeEnum(ShiftNameType)),
  shiftTypesWithTime: z
    .array(
      z.object({
        type: z.nativeEnum(ShiftNameType),
        startTime: z.string().optional(),
        endTime: z.string().optional(),
      })
    )
    .optional(),
  daysOfWeek: z.array(z.nativeEnum(DayOfTheWeek)),
  transparencyPayRates: jobShiftTransparencyPayRatesSchema.optional(),
  newGradFriendly: z.boolean().optional(),
  createdAt: z.coerce.date().optional(),
  updatedAt: z.coerce.date().optional(),
  notes: z.string().optional(),
  minimumRequiredDaysPerWeek: z.number().optional(),
});

export type JobShift = z.infer<typeof jobShiftSchema>;

export const tagIncludeSchema = z.object({
  type: z.literal(API_TYPES.tag),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    category: z.string().optional(),
    color: z.string().optional(),
    icon: z.string().optional(),
    description: z.string().optional(),
    entityType: z.string().optional(),
    metadata: z
      .object({
        workplaceType: z.string().optional(),
        filterGroup: z.string().optional(),
        order: z.coerce.number().optional(),
        abbreviation: z.string().optional(),
      })
      .optional(),
  }),
});

export type TagInclude = z.infer<typeof tagIncludeSchema>;

export const placementDataSchema = z.object({
  type: z.literal(API_TYPES.placement),
  id: z.string(),
  attributes: z.object({
    status: z.enum(PLACEMENT_STATUS),
    title: z.string(),
    jobShifts: z.array(jobShiftSchema),
    workerTypes: z.array(z.string()).optional(),
    perks: z.array(z.nativeEnum(PlacementPerk)).optional(),
    validUntil: z.coerce.date().optional(),
    createdAt: z.coerce.date().optional(),
    updatedAt: z.coerce.date().optional(),
    confirmedAt: z.coerce.date().optional(),
    originalJobLink: z.string().optional(),
    webApplicationLink: z.string().optional(),
    payRange: z
      .object({
        minPay: z
          .object({
            amountInMinorUnits: z.number(),
            currencyCode: z.string(),
          })
          .optional(),
        maxPay: z
          .object({
            amountInMinorUnits: z.number(),
            currencyCode: z.string(),
          })
          .optional(),
      })
      .optional(),
    signOnBonus: z
      .object({
        amountInMinorUnits: z.number(),
        currencyCode: z.string(),
      })
      .optional(),
    distance: z.number().optional(),
    address: z.object({
      formatted: z.string().optional(),
    }),
    coordinates: z.array(z.number()),
    schedule: z.string().optional(),
    interviewInstructions: z.string().optional(),
    transparencyPayRates: transparencyPayRatesSchema.optional(),
    // TODO: Temporarily using z.any() to bypass validation error - fix the schema to match API response
    tags: z.array(z.any()).optional(),
  }),
  meta: z.object({
    isFavorited: z.boolean().optional(),
    isLicenseEligible: z.boolean(),
    isDocumentRequirementValid: z.boolean().optional(),
    isExemptFromDocumentRequirements: z.boolean().optional(),
    allowRequestInterview: z.boolean().optional(),
    scoreBreakdown: z
      .object({
        jobType: z.number(),
        shiftType: z.number(),
        pay: z.number(),
        days: z.number(),
        location: z.number(),
        verified: z.number(),
        status: z.number(),
        verifiedPay: z.number(),
        placementTier: z.number(),
      })
      .optional(),
    score: z.number().optional(),
    baseRate: z
      .object({
        amountInMinorUnits: z.number(),
        currencyCode: z.string(),
      })
      .optional()
      .optional(),
    totalRate: z
      .object({
        amountInMinorUnits: z.number(),
        currencyCode: z.string(),
      })
      .optional(),
    totalRateBreakdown: z
      .object({
        baseRate: z.number().optional(),
        benefitsDifferential: z.number().optional(),
        weekendDifferential: z.number().optional(),
        dayDifferential: z.number().optional(),
        eveningDifferential: z.number().optional(),
        nightDifferential: z.number().optional(),
      })
      .optional(),
    matchedType: z.enum(["exact", "discovery"]).optional(),
    applicationMethod: z.nativeEnum(PlacementApplicationMethod).optional(),
    isRecruiterScreeningEnabled: z.boolean().optional(),
    allowApplicationPreferenceCollection: z.boolean().optional(),
    isEasyScheduling: z.boolean().optional(),
  }),
  relationships: z.object({
    interviewSettings: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.interviewSettings),
          id: z.string(),
        }),
      })
      .optional(),
    workerFavoritePlacement: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.workerFavoritePlacement),
          id: z.string(),
        }),
      })
      .optional(),
    workplace: z.object({
      data: z.object({
        type: z.literal(API_TYPES.workplace),
        id: z.string(),
      }),
    }),
    applications: z
      .object({
        data: z.array(
          z.object({
            type: z.literal(API_TYPES.placementApplication),
            id: z.string(),
          })
        ),
      })
      .optional(),
    tags: z
      .object({
        data: z.array(
          z.object({
            type: z.literal(API_TYPES.tag),
            id: z.string(),
          })
        ),
      })
      .optional(),
    hiringManagers: z
      .object({
        data: z.array(
          z.object({
            type: z.literal(API_TYPES.workplaceUser),
            id: z.string(),
            meta: z
              .object({
                isPrimary: z.boolean().optional(),
              })
              .optional(),
          })
        ),
      })
      .optional(),
  }),
});

const placementWorkplaceIncludeSchema = z.object({
  type: z.literal(API_TYPES.workplace),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    state: z.string(),
    type: z.string().optional(),
    photos: z.array(z.string()).optional(),
    placementSettings: z
      .object({
        isProspect: z.boolean().optional(),
      })
      .optional(),
    tmz: z.string(),
  }),
  relationships: z.object({
    workerRequirements: z.object({
      data: z.array(
        z.object({
          type: z.literal(API_TYPES.workerRequirement),
          id: z.string(),
        })
      ),
    }),
  }),
  meta: z.object({}).optional(),
});
export type PlacementIncludedWorkplace = z.infer<typeof placementWorkplaceIncludeSchema>;
const placementApplicationIncludeSchema = z.object({
  type: z.literal(API_TYPES.placementApplication),
  id: z.string(),
  attributes: z.object({
    appliedDate: z.string(),
    status: z.nativeEnum(PlacementApplicationStatus),
    updatedAt: z.string().optional(),
    workerVoiceNoteUrl: z.string().optional(),
    workerInterviewRequest: z
      .object({
        dates: z.array(z.string()),
        notes: z.string(),
      })
      .optional(),
  }),
});
export type PlacementIncludedApplication = z.infer<typeof placementApplicationIncludeSchema>;

const placementInterviewSettingsIncludeSchema = z.object({
  type: z.literal(API_TYPES.interviewSettings),
  attributes: z.object({
    scheduleInterviews: z.boolean(),
    availability: z.array(
      z.object({
        dayOfWeek: z.nativeEnum(DayOfTheWeek),
        start: z.number(),
        end: z.number(),
      })
    ),
    interviewAddress: z
      .object({
        address: z.object({
          formatted: z.string(),
        }),
        coordinates: z.object({
          latitude: z.number(),
          longitude: z.number(),
        }),
      })
      .optional(),
    interviewAddressDescription: z.string().optional(),
    interviewAddressDistance: z.number().optional(),
    selfServeInterviewBookingType: z.nativeEnum(INTERVIEW_TYPE).optional(),
  }),
  relationships: z.object({
    workplace: z.object({
      data: z.object({
        type: z.literal(API_TYPES.workplace),
        id: z.string(),
      }),
    }),
  }),
});
export type PlacementIncludedInterviewSettings = z.infer<
  typeof placementInterviewSettingsIncludeSchema
>;

const placementInterviewIncludeSchema = z.object({
  type: z.literal(API_TYPES.interview),
  id: z.string(),
  attributes: z.object({
    start: z.string(),
    end: z.string(),
    createdAt: z.string(),
    status: z.nativeEnum(InterviewStatus),
    type: z.nativeEnum(INTERVIEW_TYPE),
    scheduleStatus: z.nativeEnum(INTERVIEW_SCHEDULE_STATUS).optional(),
    verificationStatus: z.nativeEnum(INTERVIEW_VERIFICATION_STATUS).optional(),
    workerManualEta: z
      .object({
        etaAt: z.string(),
      })
      .optional(),
  }),
  relationships: z.object({
    placementApplication: z.object({
      data: z.object({
        type: z.literal(API_TYPES.placementApplication),
        id: z.string(),
      }),
    }),
    interviewAttendanceVerification: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.interviewAttendanceVerification),
          id: z.string(),
        }),
      })
      .optional(),
  }),
});
export type PlacementIncludedInterview = z.infer<typeof placementInterviewIncludeSchema>;

const workerRequirementIncludeSchema = z.object({
  type: z.literal(API_TYPES.workerRequirement),
  id: z.string(),
  attributes: z.object({
    status: z.nativeEnum(WorkerRequirementStatus),
    expiry: z.coerce.date().optional(),
    isValid: z.boolean(),
    qualifications: z.array(z.string()),
    requiredByWorkplacesCount: z.number().optional(),
    level: z.nativeEnum(WorkerRequirementMappingLevel),
  }),
  relationships: z.object({
    requirement: z.object({
      data: z.object({
        type: z.literal(API_TYPES.requirement),
        id: z.string(),
      }),
    }),
  }),
});
export type WorkerRequirementInclude = z.infer<typeof workerRequirementIncludeSchema>;

const requirementIncludeSchema = z.object({
  type: z.literal(API_TYPES.requirement),
  id: z.string(),
  attributes: z.object({
    name: z.string(),
    visibleToHCP: z.boolean().optional(),
  }),
});
export type RequirementInclude = z.infer<typeof requirementIncludeSchema>;

const candidateBanIncludeSchema = z.object({
  type: z.literal(API_TYPES.placementCandidateBan),
  id: z.string(),
  attributes: z.object({
    reason: z.string(),
    startsAt: z.string().datetime(),
    expiresAt: z.string().datetime().optional(),
    revokedAt: z.string().datetime().optional(),
    action: z.nativeEnum(PLACEMENT_CANDIDATE_BAN_ACTION).optional(),
  }),
  relationships: z.object({
    interview: z
      .object({
        data: z.object({
          type: z.literal(API_TYPES.interview),
          id: z.string(),
        }),
      })
      .optional(),
  }),
});
export type CandidateBanInclude = z.infer<typeof candidateBanIncludeSchema>;

const workplaceUserIncludeSchema = z.object({
  type: z.literal(API_TYPES.workplaceUser),
  id: z.string(),
  attributes: z.object({
    name: z.string().optional(),
    firstName: z.string().optional(),
    roles: z.array(z.string()).optional(),
    jobTitle: z.string().optional(),
  }),
});
export type WorkplaceUserInclude = z.infer<typeof workplaceUserIncludeSchema>;

// Known include type values for filtering unknown types from API response
const KNOWN_INCLUDE_TYPES = new Set<string>([
  API_TYPES.workplace,
  API_TYPES.placementApplication,
  API_TYPES.interviewSettings,
  API_TYPES.interview,
  API_TYPES.workerRequirement,
  API_TYPES.requirement,
  API_TYPES.tag,
  API_TYPES.placementCandidateBan,
  API_TYPES.workplaceUser,
]);

const knownIncludeTypesSchema = z.discriminatedUnion("type", [
  placementWorkplaceIncludeSchema,
  placementApplicationIncludeSchema,
  placementInterviewSettingsIncludeSchema,
  placementInterviewIncludeSchema,
  workerRequirementIncludeSchema,
  requirementIncludeSchema,
  tagIncludeSchema,
  candidateBanIncludeSchema,
  workplaceUserIncludeSchema,
]);

type KnownIncludeType = z.infer<typeof knownIncludeTypesSchema>;

// Filter out unknown include types before validation to prevent schema errors
// when backend returns new types that haven't been added to the FE yet.
// The .or() branch handles arrays with unknown types by filtering them out first.
// Type assertion is needed because z.or() creates a union of input types.
export const placementIncludeSchema = z.array(knownIncludeTypesSchema).or(
  z.array(z.record(z.unknown())).transform((items) => {
    const knownItems = items.filter((item) => {
      const type = item.type as string | undefined;
      const isKnown = type !== undefined && KNOWN_INCLUDE_TYPES.has(type);
      if (!isKnown && type !== undefined) {
        // eslint-disable-next-line no-console
        console.warn(`Unknown include type in placement response: "${type}"`);
      }
      return isKnown;
    });
    return z.array(knownIncludeTypesSchema).parse(knownItems);
  })
) as z.ZodType<KnownIncludeType[], z.ZodTypeDef, KnownIncludeType[]>;

export const paginatedGetPlacementsResponseSchema = z.object({
  data: z.array(placementDataSchema),
  included: placementIncludeSchema,
  links: z.object({
    nextCursor: z.string().optional(),
  }),
});

export type GetPlacementsResponse = z.infer<typeof paginatedGetPlacementsResponseSchema>;
export type PlacementData = z.infer<typeof placementDataSchema>;

export const placementResponseSchema = z.object({
  data: placementDataSchema,
  included: placementIncludeSchema,
});
export type PlacementResponse = z.infer<typeof placementResponseSchema>;
/* eslint-enable max-lines */
